import { AvatarStack, VotingAvatarStack } from "../ui/voter-avatars";
import { buildColorMap } from "../ui/voter-avatar-utils";
import { AnonymousVoterPlaceholders } from "../ui/anonymous-voter-placeholders";
import { getSelfMatchKeys } from "../../lib/anonymous-voter-identities";
import { pendingToneClass, votedToneClass } from "../../lib/poll-status-tone";

function buildEmailKeySet(users = []) {
  return new Set(
    (users || [])
      .map((entry) => String(entry?.email || "").trim().toLowerCase())
      .filter(Boolean)
  );
}

function toCount(value, fallback = 0) {
  return Number.isFinite(value) ? Number(value) : fallback;
}

// Strong-key (id/uid/email/publicIdentifier) self match, mirroring the
// foundation's anonymizeUsers self-exempt so "self" is consistent everywhere.
function userIsSelf(user, currentUser) {
  if (!currentUser) return false;
  const selfKeys = getSelfMatchKeys(currentUser);
  if (selfKeys.size === 0) return false;
  const userKeys = getSelfMatchKeys(user);
  for (const key of userKeys) {
    if (selfKeys.has(key)) return true;
  }
  return false;
}

export function PollParticipantSummary({
  eligibleUsers = [],
  votedUsers = [],
  pendingUsers = [],
  eligibleCount = null,
  votedCount = null,
  showPending = true,
  showVoteProgress = true,
  // statusKnown: whether the per-person voted/pending SPLIT is known (vote docs
  // loaded AND permitted). When false, the split renders neutral ("—") so we
  // never assert a definitive "0/N voted" before the data is known. The
  // aggregate invitee/eligible count (Layer A) is always shown regardless.
  statusKnown = true,
  showVoterIdentities = true,
  voteIdentityDisplayMode = "named",
  currentUser = null,
  visibilityHint = null,
  className = "",
  onAvatarClick,
}) {
  const effectiveEligibleCount = toCount(eligibleCount, eligibleUsers.length);
  if (effectiveEligibleCount <= 0) return null;

  const effectiveVotedCount = toCount(votedCount, votedUsers.length);
  const pendingEmailSet = buildEmailKeySet(pendingUsers);
  const pendingCount =
    pendingEmailSet.size > 0
      ? pendingEmailSet.size
      : Math.max(0, effectiveEligibleCount - effectiveVotedCount);
  const isHidden = voteIdentityDisplayMode === "hidden";
  const isAnonymous = voteIdentityDisplayMode === "anonymous";
  const participantEmails = (eligibleUsers || [])
    .map((entry) => entry?.email)
    .filter(Boolean);
  // Never build a real-email color map in hidden OR anonymous mode. In hidden
  // mode no stacks render; in anonymous mode the stacks render ALIASED users
  // whose emails miss the map anyway. Dropping it removes dead weight and
  // eliminates any future-leak risk if alias emails ever coincided with reals.
  const colorMap = isHidden || isAnonymous ? {} : buildColorMap(participantEmails);

  // Hidden-mode voter placeholder: count = OTHER voters (exclude self), and the
  // viewer's own bubble is always real if they are among the voters (R10).
  // Derive the OTHER count from the AGGREGATE effectiveVotedCount (the same
  // value driving the "N/M voted" line), not votedUsers.length, so the
  // silhouette total matches the authoritative count even when only a limited
  // set of vote docs was loaded into votedUsers.
  const votedSelfPresent = isHidden
    ? (votedUsers || []).some((u) => userIsSelf(u, currentUser))
    : false;
  const votedOtherCount = isHidden
    ? Math.max(0, effectiveVotedCount - (votedSelfPresent ? 1 : 0))
    : 0;

  return (
    <div className={`flex flex-col gap-1.5 text-xs text-slate-500 dark:text-slate-400 ${className}`.trim()}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-medium">
          {effectiveEligibleCount} invitee{effectiveEligibleCount !== 1 ? "s" : ""}:
        </span>
        {isHidden ? (
          <span>{effectiveEligibleCount}</span>
        ) : showVoterIdentities ? (
          <AvatarStack users={eligibleUsers} max={10} size={18} colorMap={colorMap} onClick={onAvatarClick} />
        ) : (
          <span>{effectiveEligibleCount}</span>
        )}
      </div>
      {/* The voted/pending SPLIT is Layer A (aggregate count) — always rendered.
          When statusKnown is false we don't yet know the per-person split, so the
          numerator reads "—" instead of asserting a definitive "0/N voted". The
          avatar STACKS (who voted) are the only piece gated by showVoteProgress. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          {/* When the split is unknown the "—" is rendered in a NEUTRAL tone (not
              the emerald "voted" tone); a sibling sr-only span annotates it so a
              screen reader announces it as "not yet available", not a count. */}
          <span
            className={`font-medium ${statusKnown ? votedToneClass : "text-muted-foreground"}`}
          >
            {statusKnown ? effectiveVotedCount : "—"}/{effectiveEligibleCount} voted:
          </span>
          {!statusKnown ? (
            <span className="sr-only">Vote status not yet available</span>
          ) : null}
          {statusKnown && showVoteProgress ? (
            isHidden ? (
              <AnonymousVoterPlaceholders
                count={votedOtherCount}
                selfUser={votedSelfPresent ? currentUser : null}
                visibilityHint={visibilityHint}
              />
            ) : showVoterIdentities ? (
              <VotingAvatarStack users={votedUsers} max={10} size={18} colorMap={colorMap} onClick={onAvatarClick} />
            ) : null
          ) : null}
        </div>
        {showPending ? (
          <div className="flex items-center gap-1.5">
            {!statusKnown ? (
              <span className="font-medium text-muted-foreground">
                —/{effectiveEligibleCount} pending:
              </span>
            ) : pendingCount > 0 ? (
                <>
                 <span className={`font-medium ${pendingToneClass}`}>
                   {pendingCount}/{effectiveEligibleCount} pending:
                 </span>
                 {showVoteProgress && !isHidden && showVoterIdentities ? (
                   <VotingAvatarStack users={pendingUsers} max={10} size={18} colorMap={colorMap} onClick={onAvatarClick} />
                 ) : null}
               </>
             ) : (
               <span className={votedToneClass}>All voted!</span>
             )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
